import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../utils/net-api';
import './EventPage.css';

const EventPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedCity, setSelectedCity] = useState('');
    const [isChanging, setIsChanging] = useState(false);
    const [events, setEvents] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const arrowRef = useRef(null);
    const filterRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    
    useEffect(() => {
        document.title = "Marlo - Events";
        loadCitiesStats();
    }, []);

    // 处理从AboutPage传来的城市参数
    useEffect(() => {
        if (location.state?.selectedCity) {
            const cityFromAbout = location.state.selectedCity;
            console.log('从AboutPage接收到城市:', cityFromAbout);
            setSelectedCity(cityFromAbout);
            // 立即加载该城市的活动数据
            loadEvents(cityFromAbout);
            // 清除location.state以避免重复处理
            navigate('/event', { replace: true, state: null });
        }
    }, [location.state, navigate]);

    useEffect(() => {
        if (selectedCity) {
            loadEvents(selectedCity);
        }
    }, [selectedCity]);

    // 加载城市统计数据 - 与iOS保持一致，显示所有API返回的城市
    const loadCitiesStats = async () => {
        try {
            const response = await axios.get(API.citiesStats);
            console.log('Cities API response:', response.data);
            if (response.data && response.data.succeed) {
                const citiesData = response.data.data || [];
                // 显示所有API返回的城市（与iOS CitiesPage保持一致）
                if (citiesData.length > 0) {
                    const cityStats = citiesData.map(c => ({
                        name: c.city,
                        count: c.count || 0
                    }));
                    setCities(cityStats);
                    // 默认选中第一个城市
                    if (cityStats.length > 0 && !selectedCity) {
                        setSelectedCity(cityStats[0].name);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load cities:', err);
        }
    };

    // 加载指定城市的活动数据
    const loadEvents = async (city) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.post(API.exploreWaves, {
                city: city,
                limit: 100,
                offset: 0
            });
            
            if (response.data && response.data.succeed) {
                setEvents(response.data.data || []);
            } else {
                setError(response.data?.message || 'Failed to load events');
                setEvents([]);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
            setError('Failed to load events');
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };
    
    const handleCitySelect = (city) => {
        // 如果正在拖拽，不处理点击事件
        if (isDragging) return;
        if (city === selectedCity) return; // 如果点击的是当前选中的，不执行任何操作
        
        setIsChanging(true);
        
        // 如果当前有箭头，先执行消失动画
        if (arrowRef.current) {
            arrowRef.current.classList.add('disappearing');
        }
        
        // 延迟切换城市，让消失动画完成
        setTimeout(() => {
            setSelectedCity(city);
            setIsChanging(false);
        }, 100);
    };

    const handleEventCardClick = (eventId) => {
        navigate(`/event/${eventId}`);
    };

    // 鼠标拖拽滚动处理函数
    const handleMouseDown = (e) => {
        if (!filterRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - filterRef.current.offsetLeft);
        setScrollLeft(filterRef.current.scrollLeft);
        filterRef.current.style.cursor = 'grabbing';
        e.preventDefault();
    };

    const handleMouseLeave = () => {
        if (!filterRef.current) return;
        setIsDragging(false);
        filterRef.current.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
        if (!filterRef.current) return;
        setIsDragging(false);
        filterRef.current.style.cursor = 'grab';
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !filterRef.current) return;
        e.preventDefault();
        const x = e.pageX - filterRef.current.offsetLeft;
        const walk = (x - startX) * 2; // 调整滚动速度
        filterRef.current.scrollLeft = scrollLeft - walk;
    };

    // 格式化日期时间
    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return 'TBD';
        try {
            const date = new Date(dateTimeStr);
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'TBD';
        }
    };

    return (
        <div className="event-page">
            <Header />
            
            {/* Background header */}
            <div className="event-bg-header"></div>
            
            <main className="event-main">
                <div className="event-content">
                    <div className="event-title">
                        <span className="title-desktop">Explore local events</span>
                        <span className="title-mobile">Popular Events</span>
                    </div>
                    <p className="event-subtitle">Find people to go with.</p>
                    
                    <div className="event-divider"></div>
                    
                    <div 
                        className="event-filters"
                        ref={filterRef}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        style={{ cursor: 'grab' }}
                    >
                        <div className="filter-buttons">
                            {cities.map((city) => (
                                <button 
                                    key={city.name}
                                    className={`filter-btn ${selectedCity === city.name ? 'filter-btn-active' : ''}`}
                                    onClick={() => handleCitySelect(city.name)}
                                >
                                    {city.name} ({city.count})
                                    {selectedCity === city.name && !isChanging && (
                                        <div 
                                            className="filter-arrow" 
                                            ref={selectedCity === city.name ? arrowRef : null}
                                        ></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Loading状态 */}
                    {loading && (
                        <div className="loading-container" style={{textAlign: 'center', padding: '40px 0'}}>
                            <p>Loading events...</p>
                        </div>
                    )}
                    
                    {/* 错误状态 */}
                    {error && (
                        <div className="error-container" style={{textAlign: 'center', padding: '40px 0', color: 'red'}}>
                            <p>Error: {error}</p>
                        </div>
                    )}
                    
                    {/* Event Cards */}
                    {!loading && !error && (
                        <div className="event-cards">
                            {events.length === 0 ? (
                                <div className="no-events" style={{textAlign: 'center', padding: '40px 0', color: '#666'}}>
                                    <p>{cities.length === 0 ? 'No events available' : `No events yet in ${selectedCity}`}</p>
                                </div>
                            ) : (
                                events.map((event) => (
                                    <div 
                                        key={event.id || event.ID} 
                                        className="event-card"
                                        onClick={() => handleEventCardClick(event.id || event.ID)}
                                    >
                                        <div className="event-card-image">
                                            {/* iOS逻辑：优先显示Media[0].FilePath，如果没有则显示WaveTypeImage */}
                                            {(event.Media && event.Media.length > 0) ? (
                                                <img 
                                                    src={event.Media[0].FilePath} 
                                                    alt={event.Title || 'Event'}
                                                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                                                    onError={(e) => {
                                                        // 如果media图片加载失败，尝试显示waveTypeImage
                                                        if (event.WaveTypeImage) {
                                                            e.target.src = event.WaveTypeImage;
                                                            e.target.onerror = () => {
                                                                e.target.style.display = 'none';
                                                            };
                                                        } else {
                                                            e.target.style.display = 'none';
                                                        }
                                                    }}
                                                />
                                            ) : event.WaveTypeImage ? (
                                                <img 
                                                    src={event.WaveTypeImage} 
                                                    alt={event.Title || 'Event'}
                                                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            ) : (
                                                <div className="event-image-placeholder"></div>
                                            )}
                                        </div>
                                        <div className="event-card-content">
                                            <div className="event-card-title">
                                                {/* iOS使用wave.Title字段 */}
                                                {event.Title || '活动标题'}
                                            </div>
                                            <div className="event-card-meta">
                                                <div className="event-meta-item">
                                                    <img 
                                                        src={require('../images/event/time.png')} 
                                                        alt="Time" 
                                                        className="event-meta-icon"
                                                    />
                                                    <span className="event-meta-text">
                                                        {/* 设计稿为「日期, 时间」，缺日期时只显示时间 */}
                                                        {[event.EventDateStr, event.EventTimeStr].filter(Boolean).join(', ') || 'Time TBD'}
                                                    </span>
                                                </div>
                                                <div className="event-meta-item">
                                                    <img 
                                                        src={require('../images/event/addr.png')} 
                                                        alt="Location" 
                                                        className="event-meta-icon"
                                                    />
                                                    <span className="event-meta-text">
                                                        {/* iOS逻辑：wave.DetailedLocation.isEmpty ? "Location TBD" : wave.DetailedLocation */}
                                                        {event.DetailedLocation || 'Location TBD'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default EventPage;
